import React from 'react';
import { motion } from 'framer-motion';
import { Brain, TrendingUp, AlertTriangle, CheckCircle, Lightbulb } from 'lucide-react';
interface Insight {
  type: 'success' | 'warning' | 'info' | 'tip';
  title: string;
  description: string;
  action?: string;
  confidence: number;
}
interface PredictiveInsightsProps {
  insights: Insight[];
  userType: 'tenant' | 'funder' | 'landlord';
}
export function PredictiveInsights({
  insights,
  userType
}: PredictiveInsightsProps) {
  const getIcon = (type: string) => {
    switch (type) {
      case 'success':
        return CheckCircle;
      case 'warning':
        return AlertTriangle;
      case 'tip':
        return Lightbulb;
      default:
        return Brain;
    }
  };
  const getColor = (type: string) => {
    switch (type) {
      case 'success':
        return 'from-green-500 to-emerald-500';
      case 'warning':
        return 'from-yellow-500 to-orange-500';
      case 'tip':
        return 'from-purple-500 to-pink-500';
      default:
        return 'from-blue-500 to-cyan-500';
    }
  };
  const getTextColor = (type: string) => {
    switch (type) {
      case 'success':
        return 'text-green-700';
      case 'warning':
        return 'text-yellow-700';
      case 'tip':
        return 'text-purple-700';
      default:
        return 'text-blue-700';
    }
  };
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-412">
      <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-blue-50" data-id="element-413">
        <div className="flex items-center" data-id="element-414">
          <div className="h-10 w-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center mr-3" data-id="element-415">
            <Brain className="h-6 w-6 text-white" data-id="element-416" />
          </div>
          <div data-id="element-417">
            <h3 className="text-lg font-bold text-gray-900" data-id="element-418">AI Insights</h3>
            <p className="text-sm text-gray-600" data-id="element-419">
              Personalized recommendations powered by machine learning
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-4" data-id="element-420">
        {insights.map((insight, idx) => {
        const Icon = getIcon(insight.type);
        return <motion.div key={idx} initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.3,
          delay: idx * 0.1
        }} className="relative overflow-hidden rounded-xl border border-gray-200 hover:shadow-md transition-shadow" data-id="element-421">
              <div className={`absolute top-0 left-0 w-1 h-full bg-gradient-to-b ${getColor(insight.type)}`} data-id="element-422"></div>

              <div className="p-4 pl-6" data-id="element-423">
                <div className="flex items-start justify-between mb-2" data-id="element-424">
                  <div className="flex items-start flex-1" data-id="element-425">
                    <Icon className={`h-5 w-5 mt-0.5 mr-3 ${getTextColor(insight.type)}`} data-id="element-426" />
                    <div className="flex-1" data-id="element-427">
                      <h4 className="font-bold text-gray-900 mb-1" data-id="element-428">
                        {insight.title}
                      </h4>
                      <p className="text-sm text-gray-600 leading-relaxed" data-id="element-429">
                        {insight.description}
                      </p>
                      {insight.action && <button className={`mt-3 text-sm font-medium ${getTextColor(insight.type)} hover:underline`} data-id="element-430">
                          {insight.action} →
                        </button>}
                    </div>
                  </div>
                  <div className="ml-4" data-id="element-431">
                    <div className="text-xs text-gray-500 text-right" data-id="element-432">
                      <span className="font-medium" data-id="element-433">{insight.confidence}%</span>
                      <p className="text-[10px]" data-id="element-434">confidence</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>;
      })}
      </div>

      <div className="p-4 bg-gray-50 border-t border-gray-100 text-center" data-id="element-435">
        <p className="text-xs text-gray-500" data-id="element-436">
          <Brain className="h-3 w-3 inline mr-1" data-id="element-437" />
          Insights updated in real-time based on your activity and market trends
        </p>
      </div>
    </div>;
}